/**
 * Sidebar Navigation for SnapHub PWA.
 *
 * Data-driven navigation using NavigationItem[] array.
 * - Desktop: Visible sidebar with Meesenburg logo, nav items, and footer
 * - Mobile: Hamburger menu that toggles sidebar open/closed
 * - Cart badge: Shows article count, blinks 2s on change, hidden when 0
 * - Auth checkmark: Real-time update on login/logout via EventBus
 *
 * Validates: Requirements 6.1, 6.2, 33.1–33.4, 36.1–36.3, 45.3
 */

import type { NavigationItem } from '../models/navigation-item.model';
import { eventBus } from '../../infrastructure/event-bus';
import { navigate, getCurrentRoute } from '../../infrastructure/router';
import { isAuthenticated } from '../../infrastructure/auth-guard';
import { storageAdapter } from '../../infrastructure/storage-adapter';
import { decodeTokenPayload, resolveRoleFromPayload } from '../../features/auth/auth.service';
import { renderCustomerSelector } from './customer-selector';
import { userPreferencesService } from '../services/user-preferences.service';
import { canAccessConfigurator } from '../../features/configurator/configurator-feature.guard';
import type { CustomerSelectorHandle } from './customer-selector';
import { customerService } from '../../features/customers/customer.service';
import { customerDataService } from '../../features/customers/customer-data.service';
import { userService } from '../../features/order/user.service';
import { cartService } from '../../features/cart/cart.service';

// ---------------------------------------------------------------------------
// State
// ---------------------------------------------------------------------------

let cartItemCount = 0;
let authenticated = isAuthenticated();
let unsubscribeAuth: (() => void) | null = null;
let unsubscribeCart: (() => void) | null = null;
let unsubscribeUser: (() => void) | null = null;
let unsubscribePreferences: (() => void) | null = null;
let blinkTimeout: ReturnType<typeof setTimeout> | null = null;
let customerSelectorHandle: CustomerSelectorHandle | null = null;

// ---------------------------------------------------------------------------
// Role check helper
// ---------------------------------------------------------------------------

function isAdminUser(): boolean {
  const token = storageAdapter.getAccessToken();
  if (!token) return false;
  const payload = decodeTokenPayload(token);
  return resolveRoleFromPayload(payload) === 'admin';
}

function isEmployeeOrAdmin(): boolean {
  const token = storageAdapter.getAccessToken();
  if (!token) return false;
  const payload = decodeTokenPayload(token);
  const role = resolveRoleFromPayload(payload);
  return role === 'employee' || role === 'admin';
}

// ---------------------------------------------------------------------------
// Navigation Items (data-driven)
// ---------------------------------------------------------------------------

function getNavigationItems(): NavigationItem[] {
  return [
    {
      id: 'cart',
      label: 'Warenkorb',
      route: '/',
      icon: 'cart',
      requiresAuth: true,
      badgeCount: () => cartItemCount,
      position: 'main',
    },
    {
      id: 'scanner',
      label: 'Scannen',
      route: '/scanner',
      icon: 'scan',
      requiresAuth: true,
      position: 'main',
    },
    // Hardware-Scanner: shown when hardware_scanner_view is enabled in user settings
    ...(userPreferencesService.isHardwareScannerViewEnabled()
      ? [
          {
            id: 'hardware-scanner',
            label: 'Hardware-Scanner',
            route: '/hardware-scanner',
            icon: 'hardware-scanner',
            requiresAuth: true,
            position: 'main' as const,
          },
        ]
      : []),
    {
      id: 'manual-entry',
      label: 'Manuelle Erfassung',
      route: '/manual-entry',
      icon: 'edit',
      requiresAuth: true,
      position: 'main',
    },
    // Schnellerfassung: available for all authenticated users
    {
      id: 'quick-entry',
      label: 'Schnellerfassung',
      route: '/quick-entry',
      icon: 'quick-entry',
      requiresAuth: true,
      position: 'main',
    },
    // Sortimente: available for all authenticated users
    {
      id: 'assortment',
      label: 'Sortimente',
      route: '/assortment',
      icon: 'assortment',
      requiresAuth: true,
      position: 'main',
    },
    // Stammdaten: only visible for admins (later: all authenticated users)
    ...(isAdminUser()
      ? [
          {
            id: 'assortment-large',
            label: 'Stammdaten',
            route: '/assortment-large',
            icon: 'assortment-large',
            requiresAuth: true,
            position: 'main' as const,
          },
        ]
      : []),
    // Konfigurator: Premium feature – restricted to whitelist during soft-launch
    ...(canAccessConfigurator()
      ? [
          {
            id: 'configurator',
            label: 'Konfigurator',
            route: '/configurator',
            icon: 'configurator',
            requiresAuth: true,
            position: 'main' as const,
            children: [
              {
                id: 'configurator-configs',
                label: 'Meine Konfigurationen',
                route: '/configurator',
                icon: 'configurator-configs',
                requiresAuth: true,
                position: 'main' as const,
              },
              {
                id: 'configurator-admin',
                label: 'Templates',
                route: '/configurator/templates',
                icon: 'configurator-templates',
                requiresAuth: true,
                position: 'main' as const,
              },
              {
                id: 'configurator-projects',
                label: 'Projekte',
                route: '/configurator/projects',
                icon: 'configurator-projects',
                requiresAuth: true,
                position: 'main' as const,
              },
              {
                id: 'configurator-exportformats',
                label: 'Exportformate',
                route: '/configurator/exportformats',
                icon: 'configurator-export',
                requiresAuth: true,
                position: 'main' as const,
              },
              {
                id: 'configurator-pricelists',
                label: 'Preislisten',
                route: '/configurator/pricelists',
                icon: 'configurator-pricelists',
                requiresAuth: true,
                position: 'main' as const,
              },
            ],
          },
        ]
      : []),
    // "Meine Kunden" moved to page-header user menu
    // Feedback moved to page-header user menu
    ...(isAdminUser()
      ? [
          {
            id: 'chat',
            label: 'AI-Assistent',
            route: '/chat',
            icon: 'chat',
            requiresAuth: true,
            position: 'main' as const,
          },
        ]
      : []),
    // Timeline moved to page-header user menu
    // Timeline verwalten moved to page-header user menu
    // Login moved to page-header user menu
    {
      id: 'imprint',
      label: 'Impressum',
      route: '/imprint',
      icon: '',
      requiresAuth: false,
      position: 'footer',
    },
  ];
}

// ---------------------------------------------------------------------------
// SVG Icons
// ---------------------------------------------------------------------------

const icons: Record<string, string> = {
  cart: `<svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="9" cy="21" r="1"/><circle cx="20" cy="21" r="1"/><path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"/></svg>`,
  scan: `<svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"/><circle cx="12" cy="13" r="4"/></svg>`,
  edit: `<svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>`,
  'quick-entry': `<svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="3" width="7" height="7" rx="1"/><rect x="14" y="3" width="7" height="7" rx="1"/><rect x="3" y="14" width="7" height="7" rx="1"/><rect x="14" y="14" width="7" height="7" rx="1"/><line x1="6.5" y1="5" x2="6.5" y2="8"/><line x1="5" y1="6.5" x2="8" y2="6.5"/></svg>`,
  'hardware-scanner': `<svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="2" y="4" width="20" height="16" rx="2"/><line x1="6" y1="8" x2="6" y2="16"/><line x1="9" y1="8" x2="9" y2="16"/><line x1="12" y1="8" x2="12" y2="16"/><line x1="14.5" y1="8" x2="14.5" y2="16"/><line x1="17" y1="8" x2="17" y2="16"/><line x1="19" y1="8" x2="19" y2="16"/><path d="M2 2 L6 4"/><path d="M2 1 L2 4"/></svg>`,
  assortment: `<svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M3 3h7v7H3z"/><path d="M14 3h7v7h-7z"/><path d="M3 14h7v7H3z"/><path d="M14 14h7v7h-7z"/></svg>`,
  'assortment-large': `<svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="2" y="2" width="20" height="20" rx="2"/><path d="M2 8h20"/><path d="M2 14h20"/><path d="M8 2v20"/></svg>`,
  customers: `<svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>`,
  user: `<svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>`,
  info: `<svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="16" x2="12" y2="12"/><line x1="12" y1="8" x2="12.01" y2="8"/></svg>`,
  hamburger: `<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="3" y1="12" x2="21" y2="12"/><line x1="3" y1="6" x2="21" y2="6"/><line x1="3" y1="18" x2="21" y2="18"/></svg>`,
  feedback: `<svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>`,
  chat: `<svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="11" width="18" height="10" rx="2"/><circle cx="12" cy="5" r="2"/><path d="M12 7v4"/><line x1="8" y1="16" x2="8" y2="16"/><line x1="16" y1="16" x2="16" y2="16"/></svg>`,
  close: `<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>`,
  help: `<svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>`,
  timeline: `<svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="12" y1="2" x2="12" y2="22"/><circle cx="12" cy="6" r="2"/><circle cx="12" cy="12" r="2"/><circle cx="12" cy="18" r="2"/><line x1="14" y1="6" x2="20" y2="6"/><line x1="4" y1="12" x2="10" y2="12"/><line x1="14" y1="18" x2="20" y2="18"/></svg>`,
  'timeline-admin': `<svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="12" y1="2" x2="12" y2="22"/><circle cx="12" cy="6" r="2"/><circle cx="12" cy="12" r="2"/><circle cx="12" cy="18" r="2"/><path d="M20 14l2 2-5 5-3-3 2-2 1 1z"/></svg>`,
  configurator: `<svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/></svg>`,
  'configurator-configs': `<svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/><polyline points="10 9 9 9 8 9"/></svg>`,
  'configurator-templates': `<svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="3" width="18" height="18" rx="2"/><line x1="3" y1="9" x2="21" y2="9"/><line x1="9" y1="21" x2="9" y2="9"/></svg>`,
  'configurator-projects': `<svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z"/></svg>`,
  'configurator-export': `<svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>`,
  'configurator-pricelists': `<svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M12 2v20M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/></svg>`,
};

// ---------------------------------------------------------------------------
// Meesenburg Logo (SVG text-based placeholder)
// ---------------------------------------------------------------------------

const logoHtml = `
  <div class="sidebar-nav__logo" aria-label="SnapHub – Meesenburg">
    <img src="/icons/SnapHubLogo.png" alt="SnapHub" class="sidebar-nav__app-logo" width="48" height="48" />
    <svg class="sidebar-nav__logo-svg" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 445.34 104.95"><defs><style>.cls-1{fill:#fff}</style></defs><g id="Ebene_2" data-name="Ebene 2"><g id="Ebene_1-2" data-name="Ebene 1"><path class="cls-1" d="M384.78,92.77a1.53,1.53,0,0,1,1.52,1.71A1.87,1.87,0,0,1,385,96.26c-1-.34-1.77-.94-1.77-1.89a1.47,1.47,0,0,1,1.52-1.6m.06,9.93a1.81,1.81,0,0,1-1.77-1.94,2.07,2.07,0,0,1,1.52-2.08,2.37,2.37,0,0,1,2,2.29,1.65,1.65,0,0,1-1.71,1.73m-.12,2.25c2.91,0,4.82-1.69,4.82-4.22a3.65,3.65,0,0,0-2.29-3.46V97.2a3.43,3.43,0,0,0,1.81-3.06c0-1.69-1.22-3.58-4.14-3.58-2.51,0-4.42,1.57-4.42,3.92a3.41,3.41,0,0,0,1.81,3v.07a3.7,3.7,0,0,0-2.23,3.49c0,1.88,1.46,3.87,4.64,3.87M376.4,90.8h-6.9l-.83,7.3a9.33,9.33,0,0,1,1.7-.13c2.5,0,3.52.88,3.52,2.31a2.23,2.23,0,0,1-2.5,2.12,6.59,6.59,0,0,1-2.83-.68l-.52,2.44a7.22,7.22,0,0,0,3.48.79c3.38,0,5.38-2.29,5.38-4.88a4.13,4.13,0,0,0-1.52-3.49,5.3,5.3,0,0,0-3.3-1,4.2,4.2,0,0,0-.81.05l.27-2.14h4.86Zm-20.46,0v2.68h5.74v0l-5.17,11.2h3.11l5.16-11.87v-2Zm-8.64,13.92h2.84V90.8h-2.43l-3.32,1.71.49,2.44,2.39-1.26h0Zm-13.85.23a2,2,0,0,0,0-4,2,2,0,0,0,0,4m-9.56-13v2.27h-1.25v2.39h1.25v4.29a4.67,4.67,0,0,0,.81,3.1,2.86,2.86,0,0,0,2.2.9,5.26,5.26,0,0,0,1.91-.28l0-2.46a4.56,4.56,0,0,1-.87.09c-.89,0-1.16-.58-1.16-1.85V96.64h2.1V94.25h-2.1v-3ZM313,104.2a6.15,6.15,0,0,0,3,.73c2.7,0,4.08-1.43,4.08-3.38,0-1.54-.77-2.55-2.56-3.24-1.16-.45-1.52-.7-1.52-1.19s.4-.86,1.12-.86a4.25,4.25,0,0,1,2,.6l.52-2.25a5.81,5.81,0,0,0-2.66-.6c-2.33,0-3.83,1.48-3.83,3.43,0,1.24.73,2.42,2.67,3.14,1.1.41,1.39.67,1.39,1.2s-.36.88-1.25.88a5.36,5.36,0,0,1-2.53-.79Zm-8.94-5.91c.08-.87.58-2.12,1.81-2.12s1.66,1.33,1.66,2.12Zm6.09,2.23a8.36,8.36,0,0,0,.1-1.29c0-2.59-1.16-5.22-4.2-5.22-3.28,0-4.76,2.93-4.76,5.59,0,3.27,1.83,5.33,5,5.33a8.43,8.43,0,0,0,3.41-.64l-.39-2.21a7.5,7.5,0,0,1-2.6.43c-1.37,0-2.56-.64-2.66-2Z"/><path class="cls-1" d="M415.69,67.19a22.81,22.81,0,0,1-2.82,7.41,4.84,4.84,0,0,1-6.07,2.11c-2.91-.92-3.47-3-3.86-4.58-.51-4.2,3.22-8.31,7.56-8.84,2.51-.31,5.61,1.76,5.19,3.9m-7,27.34a2.29,2.29,0,0,1-2.44-1.93c-.26-2.26,2.49-5.3,4.31-5.12a1.73,1.73,0,0,1,1.55,1.25c.38,1.8-2,5.67-3.42,5.8m-103-41.61c.58-3,2.18-7.42,5.12-9.46.57-.4,2-1.17,2.86-.76a2,2,0,0,1,1.08,2.54c-1.07,3.06-5.59,6.15-9.06,7.68m2.17,18.35a3.45,3.45,0,0,1-4.13-1.87c-1.08-2.79.46-5.4,3.5-6,.35,2.37,2.39,2.6,3.29,4.1s-.53,3.24-2.66,3.76M228,67.07c-.28-.36,2.89-9.14,7.22-11.8,1.13-.7,2.45-1.38,4.15-.57s1.37,2,1,3.09c-1.4,4.41-6.8,7.68-12.4,9.28M198.7,75.89c-.66-3.22-.87-6.31-4.9-8.53L197.12,64c3.85,2.66,4.74,8.55,1.58,11.87m-42.9-8.11c.93-4.6,4.61-12.05,10.34-14.65a4.17,4.17,0,0,1,3.29.24c.82.42,3,1.74.9,4.31a30.09,30.09,0,0,1-14.53,10.1M141,71.64a1.09,1.09,0,0,1-.57.71c-3.5,1.77-7.2,3.26-11.88,2.9a10.2,10.2,0,0,1-3.26-.8c-1.6-.72-2-2.17-.66-2.62,6.5-2.24,12.73-4.69,17.65-8.74-.36,2.45-.8,5.5-1.28,8.55M126.25,52.27A12.59,12.59,0,0,1,130,50a4.91,4.91,0,0,1,3.1,0,2.35,2.35,0,0,1,1.11,2c-.26,5.52-4.57,10.15-12.38,11.42a13.62,13.62,0,0,1,4.45-11.18M78.49,60.93a23.67,23.67,0,0,1-3.38,7.74c-1.35,2-1.86,2.54-3.1,2.5-1.66-.06.25-4.42,1.17-7s6.64-11.78,6.91-11.79c0,0-1,6.23-1.6,8.56M51.11,63a24.31,24.31,0,0,1-3.84,4.76,4,4,0,0,1-2.21,1,2.29,2.29,0,0,1-1.88-1.67c-.51-2.29.38-4.84,1.28-6.89a49.77,49.77,0,0,1,7.1-11.6,59.59,59.59,0,0,1,4.64-4.81c-.09,6.06-1.34,13.63-5.09,19.21M27.45,42.7c-7.47.59-14.86-4.85-16.31-12-.87-4.32-1-6.77.19-10.9,2.35-8.35,11-10.37,18-9.34,15.47,2.29,23.21,12,24.88,17.42.12.37.22.72.31,1.05a2.83,2.83,0,0,1-.81,1.86c-2.31,2.26-6.73,6.57-7.89,8l-.12.14-1.12,1.52.41-.76c.06-1.17.16-2.62.13-3.78C45,30.4,42.94,25.56,37.68,22c-2-1.38-5.5-1.58-7.39-.43S28,24.3,29.24,26.25a20.18,20.18,0,0,0,2.9,3.52c2.39,2.32,3,4.74,1.88,7.48s-3,5.17-6.57,5.45M441.7,80.4a26.71,26.71,0,0,0-5.32-1.63c-2.47-.5-5.31-.73-7.82-1C427.17,77.54,427,77,427.3,76c.86-2.59,1.45-5.67,2.43-8.23.75-1.94,2-3.78,2.52-5.76a7.58,7.58,0,0,0-.05-4.89c-1.24-2.87-5.14-3.93-9.13-2.7a8,8,0,0,1-5.21-.12,14.56,14.56,0,0,0-9.79.39c-8.62,3.66-15.95,8.82-17,16.86-.22,1.65-2.45,3.08-3.77,2.5-2.11-.91-.93-3-.44-4.15,1.72-4,3.4-8.07,5.21-12.07.75-1.67-.15-2.71-2.34-3.17-2.36-.5-3.27-1.4-3.52-3.19a6.1,6.1,0,0,0-4-5.27c-2.93-1.17-8.67-.35-10.71,1.39-4.24,3.64-4.75,7.88-2.38,12.6.33.65.79,1.46.55,2a37.69,37.69,0,0,1-3.54,7,13,13,0,0,1-4.32,3.49c-1.88,1-3.66.37-3.57-1.4a14.51,14.51,0,0,1,1.63-5c1.12-2.52,2.58-4.94,3.61-7.49.71-1.74.41-3.58-2-4.88-1.79-1-4.54-.61-6.87.85a5.85,5.85,0,0,0-1.76,1.78c-2.63,4.17-5.16,8.39-7.79,12.56a9.64,9.64,0,0,1-5.35,4.53c-1.57.43-2.4-1-1.41-3.32a6.31,6.31,0,0,1,.25-.67c1.5-2.65,3-5.3,4.5-8a16.06,16.06,0,0,0,1.67-3.41c.64-2.41-.35-4.08-2.7-5.25a7.22,7.22,0,0,0-7.42.29,48.31,48.31,0,0,0-6.34,5.23,12.92,12.92,0,0,0-2.51,3.21c-.85-3.15-3.05-4.44-5.69-5.21a9.15,9.15,0,0,0-3.79,0,6.16,6.16,0,0,0-1.06.4,49.73,49.73,0,0,0,7.39-6,16.62,16.62,0,0,0,2.93-4.29A19.58,19.58,0,0,0,327,37c-.67-6.43-7.12-9.16-14.56-6.27A30.62,30.62,0,0,0,299,40.37c-6.24,8-8.42,17-9.59,26.22a7.24,7.24,0,0,1-1.7,3.44c-.35.42-2.34,1.5-3.07,1.18-.45-.25-.12-2,.06-2.58.83-2.66,2.12-5.24,2.7-7.94a20.14,20.14,0,0,0,.34-7.54c-.64-3.63-3.21-5.46-7.56-6a14.92,14.92,0,0,0-8.94,1.33c-1.08.55-2.24,1.07-3.35-.12a7.27,7.27,0,0,0-6.53-2c-2.56.32-4.11,1.56-4.72,3.43-1.9,5.83-3.62,11.79-5.64,17.49-1.52,4.3-5.23,6.74-10.08,8.63-1.32.52-4.87.67-6.21.76-1.56.1-8.33-.77.16-2.86,6.73-1.65,10.85-5.78,14.38-10,3-3.51,4.34-7.37,3.3-11.74-.92-3.88-4.32-6.39-9.68-6.9a22.61,22.61,0,0,0-16.34,4.43,31.79,31.79,0,0,0-9.89,12.36c-.77,1.65-1.71,3.25-2.83,5.34-.39-.83-.52-1.1-.64-1.37-1.62-3.77-3.13-7.55-4.92-11.29a3.76,3.76,0,0,1-.22-3.2c1-2.73,2.12-5.52,2.09-8.35-.06-3.87-3.85-6-9.13-5.93-3.88.06-5,1.22-6.08,3.56-1.45,3.19-.09,5.34.18,8.66.07.82-1.22,3.56-1.76,4.24a90.76,90.76,0,0,1-15.88,16.12c-9.25,7-15.88,6.61-16.29,5.93-.79-1.39,5.8-.61,16.9-10.18,5.24-4.52,9-10.62,7.69-16.63A8.39,8.39,0,0,0,180,42.29a21.79,21.79,0,0,0-11-1.24c-6.1.91-10.38,3.73-14.43,6.77-1.9,1.42-4,3.39-5.8,4.88a21.12,21.12,0,0,0-.9-5.94c-1.16-4.62-5.5-7.55-11.8-8A25.87,25.87,0,0,0,119,43.4c-6.39,4.39-9.43,10.16-11,16.47a84.22,84.22,0,0,0-1.27,8.4c-.24,1.62-2.9,3.39-4.8,3.15-1.71-.22-2.59-1.65-2-3.81a26.91,26.91,0,0,1,2.14-5.49c4.58-8.59,9.34-17.13,13.86-25.74,1.73-3.28,1.18-6.72-1.38-10-2.38-3.05-6.83-4.36-11.27-3.19a56.07,56.07,0,0,0-7.52,2.42,22.55,22.55,0,0,0-4.9,3.07,22,22,0,0,0-4.36-6.74,12.4,12.4,0,0,0-11.06-3.28,19.82,19.82,0,0,0-7.27,3c-3-5.34-8.17-10.1-15.56-14.4S36.76-.66,22.57.1C11.89.68,4.8,6.22,1.61,15.73c-2.2,6.53-2.11,11,0,17.85A38.54,38.54,0,0,0,6.05,43c6.8,10.48,20,14,30.42,8.62-1.35,2.09-2.62,4.24-3.65,6.26-1.14,2.59-2.24,5.2-3.16,7.84a17.07,17.07,0,0,0,1.39,14.56c2.1,3.72,6.24,4.91,11.28,3.86a18.38,18.38,0,0,0,8.81-4.2c2.94-2.44,5-4.53,10.38-14.84,1.6-3.58,3.83-7.77,4.81-11.29,1.37-4.72,2.29-7.43,2.79-9.84a57.08,57.08,0,0,0,1.43-8.75c0-1.23,1.37-1.35,2.77-1.29,2.25.11,4,1.09,4.32,2.53a3.18,3.18,0,0,1-.52,1.88c-.72,1.39-2.08,3.77-2.83,5-1.18,1.91-3.87,6.15-6.61,11.54-1,1.88-1.5,3.63-2.42,5.5-.84,1.72-1.89,3.93-2.51,5.32C61.1,70.09,59.9,73.55,59.5,78c-.55,6.08,4.17,7.1,7.61,7h.13C73,84.67,78.51,79.15,81.8,73.64c2.35-3.93,8.43-22.27,9.87-28.48.64-2.75,1.21-5,4.31-6a3,3,0,0,1,2.68,0,1.46,1.46,0,0,1,.82,1.71c-3.12,6.26-6.73,13.46-9.8,19.29-4.08,7.91-5,13.47-4.07,17.5.57,2.47,1.29,4.88,2.67,6,1.58,1.26,3.17,1.48,6.9.91,3.32-.5,6.37-2.26,9.3-3.42s5.16-2.07,8.16.43c6,5,17.62,4.45,29.49-.64a2.88,2.88,0,0,1,3.33.29,21.91,21.91,0,0,0,3.92,2.27c7.34,3,23.87,1.35,29.16-4.71,1-1.14,2.86-2.8,2.86-2.8s-.45,2.91.95,4.44a11.43,11.43,0,0,0,4.13,3l.35.13c.12,0,.23.09.35.12l.44.13a36.27,36.27,0,0,0,5,.8,23.69,23.69,0,0,0,14.76-4,17.42,17.42,0,0,0,5.78-5.74c1.82,5.35,7.93,8.67,14.53,9.63,4.55.67,8.69-.27,12.64-1.54,2-.64,4-1.32,5.94-2,2.49,4.06,7.16,5.21,11.53,2.78a7.13,7.13,0,0,0,3.1-3.48c1.41-3.74,2.21-7.65,3.64-11.38a52.94,52.94,0,0,1,4.24-8,2.35,2.35,0,0,1,2.52-1.21c1.56.11,2.11.74,2.27,1.45a5.91,5.91,0,0,1-.1,3.15c-.4,1.12-1.36,3.32-1.88,4.42a20.06,20.06,0,0,0-2,10.93c.52,4.76,3.87,5.83,10,4.66,3.77-.72,6.26-2,9.54-3.56,1.25-.58,1.7-.48,3,.37,8.83,5.72,14.16,4.19,20.38.53A28.12,28.12,0,0,0,317.17,78c1.48-1.36,2.7-2.87,4.08-4.38A44.47,44.47,0,0,0,323.54,80c1.84,3.51,6.11,5.37,10.91,4.65a63.44,63.44,0,0,0,9.14-2.36,4,4,0,0,1,3.71.36,18.78,18.78,0,0,0,5.1,2,15.41,15.41,0,0,0,7-.23l.61-.19.73-.28.13-.06c.21-.09.42-.18.63-.29l.25-.13c.2-.1.41-.21.61-.33l0,0c3.83-2.26,6.85-6.41,10.53-11.48,3.94-5.43,4.3-7,6.38-6.85s-2.54,4.43-5,10.8,4.3,8.92,7.27,9.29a12,12,0,0,0,3.33-.29c3.78-.73,8.62-3.75,8.62-3.75a10.79,10.79,0,0,0,3.51,2,16.66,16.66,0,0,0,3.35.75,4.15,4.15,0,0,0-2.53,1.7,15,15,0,0,0-2.4,6.31c-1,5.36,4.13,11.13,11,11.67,5.65.45,10.19-.88,12.95-4.2,2.05-2.45,4.93-8.42,6.27-11.14,0,0,3.83.47,5.1.55a88.23,88.23,0,0,0,10.29,0c2.61-.16,4-1.24,4.26-3.31s-.86-3.57-3.59-4.72"/><path class="cls-1" d="M293.3,96.06c1.15,0,2.09-.69,2.09-1.54S294.45,93,293.3,93H30.67c-1.15,0-2.09.69-2.09,1.55s.94,1.54,2.09,1.54Z"/></g></g></svg>
  </div>
`;

// ---------------------------------------------------------------------------
// Render helpers
// ---------------------------------------------------------------------------

function getSelectedCustomerName(): string | null {
  if (!authenticated || !isEmployeeOrAdmin()) return null;
  const customerNumber = userService.getCustomerNumber();
  if (!customerNumber) return null;

  const customers = customerService.getCustomers();
  const match = customers.find((c) => c.customer_id === customerNumber);
  return match ? match.name : customerNumber;
}

function renderNavItem(item: NavigationItem, currentRoute: string): string {
  const isActive = item.route === currentRoute;
  const hasChildren = item.children && item.children.length > 0;
  const isChildActive = hasChildren && item.children!.some((c) => c.route === currentRoute);
  const isExpanded = isActive || isChildActive;
  const activeClass = isActive ? ' sidebar-nav__item--active' : '';
  const iconSvg = icons[item.icon] ?? '';

  const iconHtml = iconSvg
    ? `<span class="sidebar-nav__icon">${iconSvg}</span>`
    : '';

  let badgeHtml = '';
  if (item.badgeCount) {
    const count = item.badgeCount();
    if (count > 0) {
      badgeHtml = `<span class="sidebar-nav__badge badge" data-badge-id="${item.id}">${count}</span>`;
    }
  }

  let checkmarkHtml = '';
  if (item.showCheckmark && item.showCheckmark()) {
    checkmarkHtml = `<span class="sidebar-nav__checkmark" aria-label="Angemeldet">✓</span>`;
  }

  // Show selected customer name subtly below the cart item
  let customerSubtitleHtml = '';
  if (item.id === 'cart') {
    const customerName = getSelectedCustomerName();
    if (customerName) {
      customerSubtitleHtml = `<span class="sidebar-nav__customer-subtitle" title="${customerName}">${customerName}</span>`;
    }
  }

  // Render sub-items if present
  let childrenHtml = '';
  if (hasChildren) {
    const chevronSvg = `<svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="sidebar-nav__chevron${isExpanded ? ' sidebar-nav__chevron--open' : ''}"><polyline points="6 9 12 15 18 9"/></svg>`;
    const subItems = item.children!.map((child) => {
      const childActive = child.route === currentRoute;
      const childIcon = icons[child.icon] ?? '';
      return `
        <li class="sidebar-nav__subitem${childActive ? ' sidebar-nav__subitem--active' : ''}">
          <a href="#${child.route}" class="sidebar-nav__sublink" data-route="${child.route}" aria-current="${childActive ? 'page' : 'false'}">
            ${childIcon ? `<span class="sidebar-nav__subicon">${childIcon}</span>` : ''}
            <span class="sidebar-nav__sublabel">${child.label}</span>
          </a>
        </li>
      `;
    }).join('');

    childrenHtml = `
      <ul class="sidebar-nav__submenu${isExpanded ? ' sidebar-nav__submenu--open' : ''}" role="list">
        ${subItems}
      </ul>
    `;

    return `
      <li class="sidebar-nav__item sidebar-nav__item--parent${activeClass}${isExpanded ? ' sidebar-nav__item--expanded' : ''}" data-nav-id="${item.id}">
        <a href="#${item.route}" class="sidebar-nav__link" data-route="${item.route}" aria-current="${isActive ? 'page' : 'false'}">
          <span class="sidebar-nav__link-row">
            ${iconHtml}
            <span class="sidebar-nav__label">${item.label}</span>
            ${chevronSvg}
          </span>
        </a>
        ${childrenHtml}
      </li>
    `;
  }

  return `
    <li class="sidebar-nav__item${activeClass}" data-nav-id="${item.id}">
      <a href="#${item.route}" class="sidebar-nav__link${customerSubtitleHtml ? ' sidebar-nav__link--with-subtitle' : ''}" data-route="${item.route}" aria-current="${isActive ? 'page' : 'false'}">
        <span class="sidebar-nav__link-row">
          ${iconHtml}
          <span class="sidebar-nav__label">${item.label}</span>
          ${badgeHtml}
          ${checkmarkHtml}
        </span>
        ${customerSubtitleHtml}
      </a>
    </li>
  `;
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Render the sidebar navigation into the given sidebar element.
 * Called by the router after each layout change.
 */
export function renderSidebarNavigation(sidebarEl: HTMLElement): void {
  // Destroy existing customer selector before re-rendering
  if (customerSelectorHandle) {
    customerSelectorHandle.destroy();
    customerSelectorHandle = null;
  }

  const items = getNavigationItems();
  const currentRoute = getCurrentRoute();

  const mainItems = items.filter((i) => i.position === 'main');
  const footerItems = items.filter((i) => i.position === 'footer');

  sidebarEl.innerHTML = `
    ${logoHtml}
    <nav class="sidebar-nav" aria-label="Hauptnavigation">
      <ul class="sidebar-nav__list" role="list">
        ${mainItems.map((item) => renderNavItem(item, currentRoute)).join('')}
      </ul>
    </nav>
    <div class="sidebar-nav__footer">
      <div class="sidebar-nav__footer-row">
        <nav aria-label="Footer-Navigation">
          <ul class="sidebar-nav__list sidebar-nav__list--footer" role="list">
            ${footerItems.map((item) => renderNavItem(item, currentRoute)).join('')}
          </ul>
        </nav>
        <span class="sidebar-nav__footer-separator">·</span>
        <span class="sidebar-nav__copyright">Meesenburg seit 1758</span>
      </div>
    </div>
  `;

  // Render global customer selector for employees/admins
  if (authenticated && isEmployeeOrAdmin()) {
    renderSidebarCustomerSelector(sidebarEl);
  }

  // Attach click handlers for navigation
  sidebarEl.querySelectorAll<HTMLAnchorElement>('.sidebar-nav__link').forEach((link) => {
    link.addEventListener('click', (e) => {
      e.preventDefault();
      const route = link.dataset.route;
      if (!route) return;

      // Toggle submenu for parent items
      const parentItem = link.closest('.sidebar-nav__item--parent');
      if (parentItem) {
        const submenu = parentItem.querySelector('.sidebar-nav__submenu');
        const chevron = parentItem.querySelector('.sidebar-nav__chevron');
        if (submenu) {
          submenu.classList.toggle('sidebar-nav__submenu--open');
          parentItem.classList.toggle('sidebar-nav__item--expanded');
        }
        if (chevron) {
          chevron.classList.toggle('sidebar-nav__chevron--open');
        }
      }

      navigate(route);
      // Close mobile sidebar after navigation
      closeMobileSidebar();
    });
  });

  // Attach click handlers for sub-items
  sidebarEl.querySelectorAll<HTMLAnchorElement>('.sidebar-nav__sublink').forEach((link) => {
    link.addEventListener('click', (e) => {
      e.preventDefault();
      const route = link.dataset.route;
      if (route) {
        navigate(route);
        closeMobileSidebar();
      }
    });
  });
}

/**
 * Render the global customer selector in the sidebar.
 * Placed below the navigation, above the footer.
 * Only visible for employees/admins.
 */
function renderSidebarCustomerSelector(sidebarEl: HTMLElement): void {
  const nav = sidebarEl.querySelector('.sidebar-nav');
  const footer = sidebarEl.querySelector('.sidebar-nav__footer');
  if (!nav || !footer) return;

  const wrapper = document.createElement('div');
  wrapper.className = 'sidebar-nav__customer-selector';

  const label = document.createElement('label');
  label.className = 'sidebar-nav__customer-label';
  label.textContent = 'Kunde';
  wrapper.appendChild(label);

  const selectorContainer = document.createElement('div');
  selectorContainer.className = 'sidebar-nav__customer-container';

  // Ensure customers are loaded
  if (!customerService.isLoaded()) {
    customerService.loadCustomers().catch(() => {
      // Silently handle – selector will show fallback input
    });
  }

  customerSelectorHandle = renderCustomerSelector(selectorContainer, {
    value: userService.getCustomerNumber() || '',
    onChange: (customerId) => {
      userService.setCustomerNumber(customerId);
      // Load the separate cart for the selected customer
      if (customerId) {
        cartService.loadCart(customerId).catch(() => {
          // Silently handle – cart will be empty until next successful load
        });
        // Load customer address data (Auftraggeber + Lieferadressen)
        customerDataService.loadCustomerData(customerId).catch(() => {
          // Silently handle – address section will show empty state
        });
      } else {
        cartService.reset();
        customerDataService.reset();
      }
    },
  });

  wrapper.appendChild(selectorContainer);

  // Insert between nav and footer
  sidebarEl.insertBefore(wrapper, footer);
}

/**
 * Add the hamburger toggle button into the main-content header area.
 * Only visible on mobile (< 768px via CSS).
 */
export function renderHamburgerButton(mainContentEl: HTMLElement): void {
  // Only add if not already present
  if (mainContentEl.querySelector('.sidebar-nav__hamburger')) return;

  const btn = document.createElement('button');
  btn.className = 'sidebar-nav__hamburger btn-icon';
  btn.setAttribute('aria-label', 'Menü öffnen');
  btn.setAttribute('aria-expanded', 'false');
  btn.innerHTML = icons.hamburger;
  btn.type = 'button';

  btn.addEventListener('click', () => {
    toggleMobileSidebar();
  });

  // Prepend to main content
  mainContentEl.insertBefore(btn, mainContentEl.firstChild);
}

/**
 * Initialize event bus listeners for auth and cart changes.
 * Should be called once at app startup.
 */
export function initSidebarListeners(): void {
  // Clean up previous listeners
  cleanupSidebarListeners();

  unsubscribeAuth = eventBus.on<{ authenticated: boolean }>('auth-state-changed', (detail) => {
    authenticated = detail.authenticated;
    rerenderSidebar();
  });

  unsubscribeCart = eventBus.on<{ itemCount: number }>('cart-changed', (detail) => {
    const previousCount = cartItemCount;
    cartItemCount = detail.itemCount ?? 0;
    rerenderSidebar();

    // Blink badge when count increases
    if (cartItemCount > previousCount) {
      triggerBadgeBlink();
    }
  });

  unsubscribeUser = eventBus.on('user-changed', () => {
    rerenderSidebar();
  });

  unsubscribePreferences = eventBus.on('user-preferences-changed', () => {
    rerenderSidebar();
  });
}

/**
 * Clean up event bus listeners.
 */
export function cleanupSidebarListeners(): void {
  if (unsubscribeAuth) {
    unsubscribeAuth();
    unsubscribeAuth = null;
  }
  if (unsubscribeCart) {
    unsubscribeCart();
    unsubscribeCart = null;
  }
  if (unsubscribeUser) {
    unsubscribeUser();
    unsubscribeUser = null;
  }
  if (unsubscribePreferences) {
    unsubscribePreferences();
    unsubscribePreferences = null;
  }
  if (blinkTimeout) {
    clearTimeout(blinkTimeout);
    blinkTimeout = null;
  }
  if (customerSelectorHandle) {
    customerSelectorHandle.destroy();
    customerSelectorHandle = null;
  }
}

// ---------------------------------------------------------------------------
// Internal helpers
// ---------------------------------------------------------------------------

function rerenderSidebar(): void {
  const sidebarEl = document.getElementById('sidebar');
  if (sidebarEl) {
    renderSidebarNavigation(sidebarEl);
  }
}

export function toggleMobileSidebar(): void {
  const sidebarEl = document.getElementById('sidebar');
  if (!sidebarEl) return;

  const isOpen = sidebarEl.classList.toggle('sidebar--open');
  const overlay = document.getElementById('sidebar-overlay');

  // Update hamburger button aria-expanded (check both old and new locations)
  const hamburger = document.querySelector('.page-header__hamburger') ?? document.querySelector('.sidebar-nav__hamburger');
  if (hamburger) {
    hamburger.setAttribute('aria-expanded', String(isOpen));
    hamburger.setAttribute('aria-label', isOpen ? 'Menü schließen' : 'Menü öffnen');
  }

  if (isOpen) {
    // Add overlay
    if (!overlay) {
      const newOverlay = document.createElement('div');
      newOverlay.id = 'sidebar-overlay';
      newOverlay.className = 'sidebar-overlay';
      newOverlay.addEventListener('click', closeMobileSidebar);
      document.body.appendChild(newOverlay);
    }
  } else {
    removeOverlay();
  }
}

export function closeMobileSidebar(): void {
  const sidebarEl = document.getElementById('sidebar');
  if (sidebarEl) {
    sidebarEl.classList.remove('sidebar--open');
  }

  const hamburger = document.querySelector('.page-header__hamburger') ?? document.querySelector('.sidebar-nav__hamburger');
  if (hamburger) {
    hamburger.setAttribute('aria-expanded', 'false');
    hamburger.setAttribute('aria-label', 'Menü öffnen');
  }

  removeOverlay();
}

function removeOverlay(): void {
  const overlay = document.getElementById('sidebar-overlay');
  if (overlay) {
    overlay.remove();
  }
}

function triggerBadgeBlink(): void {
  if (blinkTimeout) {
    clearTimeout(blinkTimeout);
  }

  const badgeEl = document.querySelector('.sidebar-nav__badge[data-badge-id="cart"]');
  if (badgeEl) {
    badgeEl.classList.remove('blink');
    // Force reflow to restart animation
    void (badgeEl as HTMLElement).offsetWidth;
    badgeEl.classList.add('blink');

    blinkTimeout = setTimeout(() => {
      badgeEl.classList.remove('blink');
      blinkTimeout = null;
    }, 2000);
  }
}

// ---------------------------------------------------------------------------
// Exported for testing
// ---------------------------------------------------------------------------

export { getNavigationItems, cartItemCount as _cartItemCount };

/**
 * Reset internal state (for testing only).
 */
export function _resetState(): void {
  cartItemCount = 0;
  authenticated = false;
  cleanupSidebarListeners();
}

/**
 * Set cart item count directly (for testing only).
 */
export function _setCartItemCount(count: number): void {
  cartItemCount = count;
}

/**
 * Set authenticated state directly (for testing only).
 */
export function _setAuthenticated(auth: boolean): void {
  authenticated = auth;
}
